/**
 * HollyChatPanel — slide-in conversational panel.
 *
 *   Desktop (>768px): 400px wide, right edge, full height, slide from right
 *   Mobile (≤768px):  100vw, full screen, slide from bottom
 *   Overlay: rgba(0,0,0,0.15) desktop only, click to close
 *   Header: FloxOLogo + "Holly AI" + "● Online" + close button
 *   Input: full-width + 38px send button
 *   Escape key closes panel
 *
 * Register CTAs are detected by `cta.url` OR a known `cta.action` id. In the
 * controlled (registration) flow they fire `onReadyToRegister(threadId)` for an
 * in-page handoff; otherwise they fall back to opening the register URL / page.
 */

import { useRef, useEffect, useState } from 'react';
import { useHolly, HOLLY_WELCOME_CHIPS } from './HollyContext';
import FloxOLogo from './FloxOLogo';
import HollyMessageBubble from './HollyMessage';
import HollyTypingIndicator from './HollyTypingIndicator';
import HollyQuickReplies from './HollyQuickReplies';
import HollyWarehouseDetailPanel from './HollyWarehouseDetailPanel';
import type { HollySuggestedCta } from './types';

/* Register CTA action ids emitted by the Holly v3 agent. Mirrors the set used
 * by the legacy AISidebar so behaviour is identical. */
const REGISTER_ACTION_IDS: ReadonlySet<string> = new Set([
  'register',
  'wh_register',
  'tp_continue_flox',
  'lsp_register',
]);

export default function HollyChatPanel() {
  const {
    isChatOpen,
    closeChat,
    messages,
    isLoading,
    sendMessage,
    onTrackEvent,
    selectedWarehouse,
    threadId,
    onReadyToRegister,
    welcomeChipsEnabled,
  } = useHolly();

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');

  /* Focus input when panel opens */
  useEffect(() => {
    if (isChatOpen) {
      // Small delay to let animation start
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isChatOpen]);

  /* Scroll to bottom on new messages */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  /* Escape closes the panel — but when a warehouse is selected, the detail
   * panel's own (capture-phase) handler returns to chat first; we must NOT
   * also close the whole chat in that case. */
  useEffect(() => {
    if (!isChatOpen) return undefined;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !selectedWarehouse) closeChat();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isChatOpen, closeChat, selectedWarehouse]);

  const handleSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;
    sendMessage(trimmed);
    setInputValue('');
    onTrackEvent?.('holly_chat_message', {
      message_length: trimmed.length,
      is_quick_reply: false,
    });
  };

  const handleQuickReply = (cta: HollySuggestedCta) => {
    if (isLoading) return;
    onTrackEvent?.('holly_chip_click', {
      chip_text: cta.message,
      location: 'chat_panel',
    });

    /* Register CTA: detect both the pre-filled `url` (website-style) and the
     * v3 `action` ids. In the controlled registration flow, hand off in-page
     * with the current threadId; otherwise fall back to the register URL/page. */
    const isRegisterCta =
      !!cta.url || (cta.action != null && REGISTER_ACTION_IDS.has(cta.action));
    if (isRegisterCta) {
      if (onReadyToRegister && threadId) {
        onReadyToRegister(threadId);
        return;
      }
      if (cta.url) {
        window.open(cta.url, '_blank', 'noopener,noreferrer');
        return;
      }
      window.location.href = '/register';
      return;
    }

    /* All other CTAs send the label back to Holly as the next user message. */
    sendMessage(cta.message);
  };

  /* Get suggested CTAs from the last Holly message */
  const lastHollyMessage = [...messages]
    .reverse()
    .find((m) => m.role === 'holly');
  const showQuickReplies =
    lastHollyMessage?.suggestedCtas &&
    lastHollyMessage.suggestedCtas.length > 0 &&
    !isLoading;

  /* Onboarding chips render below the welcome card until the user starts a
   * conversation — suppressed in controlled/seeded mode (registration). */
  const showWelcomeChips =
    welcomeChipsEnabled && messages.length === 0 && !isLoading;

  if (!isChatOpen) return null;

  return (
    <>
      {/* Overlay — desktop only. */}
      <div
        className='hidden md:block fixed inset-0 holly-animate'
        style={{
          zIndex: 299,
          backgroundColor: 'rgba(0,0,0,0.15)',
          animation: 'holly-fade-in var(--duration-normal) var(--ease-out)',
        }}
        onClick={closeChat}
        aria-hidden='true'
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role='dialog'
        aria-modal='true'
        aria-label='Holly AI Chat'
        className='holly-animate'
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 300,
          width: '100vw',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Poppins', sans-serif",
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          animation: 'holly-slide-in-up 350ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Desktop override: 400px wide, slide from right */}
        <style>{`
          @media (min-width: 769px) {
            [aria-label="Holly AI Chat"] {
              width: 400px !important;
              animation: holly-slide-in-right 350ms cubic-bezier(0.16,1,0.3,1) !important;
            }
          }
          @media (prefers-reduced-motion: reduce) {
            [aria-label="Holly AI Chat"] {
              animation: none !important;
            }
          }
        `}</style>

        {/* ── Header ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #E2E8F0',
            flexShrink: 0,
          }}
        >
          <FloxOLogo size={28} variant='colour' />
          <span
            style={{
              marginLeft: 10,
              fontWeight: 700,
              fontSize: 15,
              color: '#000066',
            }}
          >
            Holly AI
          </span>
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              color: '#00C896',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#00C896',
                display: 'inline-block',
              }}
            />
            Online
          </span>
          <button
            type='button'
            onClick={closeChat}
            aria-label='Close chat'
            style={{
              marginLeft: 'auto',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #E2E8F0',
              borderRadius: 6,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: 18,
              color: '#1E1E2F',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* ── Body: messages list ── */}
        <div
          className='hide-scrollbar'
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 12px 8px',
          }}
        >
          {/* Static welcome card — always visible at the top of the conversation. */}
          <div
            style={{
              backgroundColor: '#EFF6FF',
              borderRadius: 12,
              padding: 16,
              fontSize: 14,
              lineHeight: '20px',
              color: '#1E1E2F',
              fontFamily: "'Poppins', sans-serif",
              marginBottom: 16,
            }}
          >
            <span
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Hi, I&apos;m Holly, FLOX&apos;s AI assistant.
            </span>
            <span>
              Ask me about capacity, service providers or general logistics. If
              you&apos;d like the FLOX team to follow up, share your details and
              I&apos;ll pass them on.
              <br />
              I&apos;m automated, not human. I can occasionally get things
              wrong, so verify anything important.
            </span>
          </div>

          {/* Static onboarding chips — only before the first user message. */}
          {showWelcomeChips && (
            <HollyQuickReplies
              ctas={HOLLY_WELCOME_CHIPS}
              onSelect={handleQuickReply}
              disabled={isLoading}
            />
          )}

          {messages.map((msg) => (
            <HollyMessageBubble key={msg.id} message={msg} />
          ))}

          {isLoading && (
            <HollyTypingIndicator
              lastUserMessage={
                [...messages].reverse().find((m) => m.role === 'user')?.content
              }
            />
          )}

          {/* Quick replies below the last Holly message */}
          {showQuickReplies && (
            <HollyQuickReplies
              ctas={lastHollyMessage!.suggestedCtas!}
              onSelect={handleQuickReply}
              disabled={isLoading}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Input bar ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            borderTop: '1px solid #E2E8F0',
            flexShrink: 0,
          }}
        >
          <input
            ref={inputRef}
            type='text'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder='Ask Holly anything…'
            disabled={isLoading}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: 14,
              fontFamily: "'Poppins', sans-serif",
              color: '#1E1E2F',
              backgroundColor: 'transparent',
            }}
          />
          <button
            type='button'
            onClick={handleSubmit}
            disabled={isLoading || !inputValue.trim()}
            aria-label='Send message'
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,
              backgroundColor:
                isLoading || !inputValue.trim() ? '#94A3B8' : '#2563EB',
              border: 'none',
              cursor: isLoading || !inputValue.trim() ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition:
                'background-color var(--duration-instant) var(--ease-out)',
            }}
          >
            {/* Send arrow icon */}
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              aria-hidden='true'
            >
              <path
                d='M22 2L11 13'
                stroke='white'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M22 2L15 22L11 13L2 9L22 2Z'
                stroke='white'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Warehouse detail modal/sheet — self-renders only when a warehouse is
       * selected. Sibling of the chat panel so it portals above without being
       * trapped by the chat panel's transform-containing-block. */}
      <HollyWarehouseDetailPanel />
    </>
  );
}
